'use client';
import React, { useState } from 'react';
import LeadCaptureModal from './LeadCaptureModal';

type LoanType = 'home' | 'personal' | 'business' | 'car';

const loanTypes: { key: LoanType; label: string; minRate: number; maxAmount: number }[] = [
  { key: 'home', label: 'Home Loan', minRate: 7.10, maxAmount: 50000000 },
  { key: 'personal', label: 'Personal Loan', minRate: 9.98, maxAmount: 4000000 },
  { key: 'business', label: 'Business Loan', minRate: 11.00, maxAmount: 5000000 },
  { key: 'car', label: 'Car Loan', minRate: 7.25, maxAmount: 2000000 },
];

function formatCurrency(amount: number): string {
  if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(2)} Cr`;
  if (amount >= 100000) return `₹${(amount / 100000).toFixed(2)} L`;
  if (amount >= 1000) return `₹${(amount / 1000).toFixed(1)}K`;
  return `₹${amount}`;
}

function formatINR(amount: number): string {
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount);
}

export default function EMICalculator() {
  const [loanType, setLoanType] = useState<LoanType>('home');
  const [principal, setPrincipal] = useState(3000000);
  const [rate, setRate] = useState(7.10);
  const [tenure, setTenure] = useState(240);
  const [modalOpen, setModalOpen] = useState(false);

  const selectedLoan = loanTypes.find(l => l.key === loanType)!;

  // EMI calculation
  const monthlyRate = rate / 12 / 100;
  const emi = monthlyRate === 0
    ? principal / tenure
    : (principal * monthlyRate * Math.pow(1 + monthlyRate, tenure)) / (Math.pow(1 + monthlyRate, tenure) - 1);
  const totalPayment = emi * tenure;
  const totalInterest = totalPayment - principal;
  const interestPercent = Math.round((totalInterest / totalPayment) * 100);
  const principalPercent = 100 - interestPercent;

  return (
    <>
      <LeadCaptureModal isOpen={modalOpen} onClose={() => setModalOpen(false)} service={`Apply for ${selectedLoan.label}`} />
      <section id="calculators" className="py-10 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">EMI Calculator</h2>
            <p className="text-sm text-gray-500 max-w-2xl mx-auto">
              Calculate your monthly EMI for any loan type. Adjust the loan amount, interest rate, and tenure to find the best plan for you.
            </p>
          </div>

          {/* Loan type tabs */}
          <div className="flex flex-wrap gap-2 justify-center mb-8">
            {loanTypes.map((lt) => (
              <button
                key={lt.key}
                onClick={() => {
                  setLoanType(lt.key);
                  setRate(lt.minRate);
                  setPrincipal(Math.min(principal, lt.maxAmount));
                }}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                  loanType === lt.key
                    ? 'bg-primary text-white shadow-md'
                    : 'bg-white text-gray-600 border border-gray-200 hover:border-primary hover:text-primary'
                }`}
              >
                {lt.label}
                {lt.key === 'home' && (
                  <span className="ml-1.5 text-xs bg-yellow-400 text-yellow-900 font-bold px-1.5 py-0.5 rounded-full">7.10%</span>
                )}
              </button>
            ))}
          </div>

          <div className="bg-white rounded-2xl shadow-md overflow-hidden border border-gray-200">
            <div className="grid grid-cols-1 lg:grid-cols-2">
              {/* Left - Inputs */}
              <div className="p-6 lg:p-8 border-b lg:border-b-0 lg:border-r border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{selectedLoan.label} EMI Calculator</h3>

                {/* Loan Amount */}
                <div className="mb-6">
                  <div className="flex justify-between items-center mb-2">
                    <label className="text-sm font-medium text-gray-600">Loan Amount</label>
                    <div className="flex items-center gap-1">
                      <span className="text-sm text-gray-600">₹</span>
                      <input
                        type="number"
                        value={principal}
                        onChange={(e) => setPrincipal(Math.min(Number(e.target.value), selectedLoan.maxAmount))}
                        className="w-24 text-right text-sm font-semibold text-primary border-b border-primary outline-none bg-transparent"
                      />
                    </div>
                  </div>
                  <input
                    type="range"
                    min={10000}
                    max={selectedLoan.maxAmount}
                    step={10000}
                    value={principal}
                    onChange={(e) => setPrincipal(Number(e.target.value))}
                    className="w-full h-2 rounded-full appearance-none cursor-pointer"
                    style={{
                      background: `linear-gradient(to right, #E07B1A ${(principal / selectedLoan.maxAmount) * 100}%, #E5E7EB ${(principal / selectedLoan.maxAmount) * 100}%)`
                    }}
                  />
                  <div className="flex justify-between text-xs text-gray-400 mt-1">
                    <span>₹10K</span>
                    <span>{formatCurrency(selectedLoan.maxAmount)}</span>
                  </div>
                </div>

                {/* Interest Rate */}
                <div className="mb-6">
                  <div className="flex justify-between items-center mb-2">
                    <label className="text-sm font-medium text-gray-600">Interest Rate (p.a.)</label>
                    <div className="flex items-center gap-1">
                      <input
                        type="number"
                        value={rate}
                        step={0.1}
                        min={1}
                        max={36}
                        onChange={(e) => setRate(Number(e.target.value))}
                        className="w-16 text-right text-sm font-semibold text-primary border-b border-primary outline-none bg-transparent"
                      />
                      <span className="text-sm text-gray-600">%</span>
                    </div>
                  </div>
                  <input
                    type="range"
                    min={1}
                    max={36}
                    step={0.1}
                    value={rate}
                    onChange={(e) => setRate(Number(e.target.value))}
                    className="w-full h-2 rounded-full appearance-none cursor-pointer"
                    style={{
                      background: `linear-gradient(to right, #E07B1A ${((rate - 1) / 35) * 100}%, #E5E7EB ${((rate - 1) / 35) * 100}%)`
                    }}
                  />
                  <div className="flex justify-between text-xs text-gray-400 mt-1">
                    <span>1%</span>
                    <span>36%</span>
                  </div>
                </div>

                {/* Tenure */}
                <div className="mb-6">
                  <div className="flex justify-between items-center mb-2">
                    <label className="text-sm font-medium text-gray-600">Loan Tenure</label>
                    <div className="flex items-center gap-1">
                      <input
                        type="number"
                        value={tenure}
                        min={1}
                        max={360}
                        onChange={(e) => setTenure(Number(e.target.value))}
                        className="w-16 text-right text-sm font-semibold text-primary border-b border-primary outline-none bg-transparent"
                      />
                      <span className="text-sm text-gray-600">Months</span>
                    </div>
                  </div>
                  <input
                    type="range"
                    min={1}
                    max={360}
                    step={1}
                    value={tenure}
                    onChange={(e) => setTenure(Number(e.target.value))}
                    className="w-full h-2 rounded-full appearance-none cursor-pointer"
                    style={{
                      background: `linear-gradient(to right, #E07B1A ${(tenure / 360) * 100}%, #E5E7EB ${(tenure / 360) * 100}%)`
                    }}
                  />
                  <div className="flex justify-between text-xs text-gray-400 mt-1">
                    <span>1 Mo</span>
                    <span>360 Mo</span>
                  </div>
                </div>

                <button
                  onClick={() => setModalOpen(true)}
                  className="w-full py-3 bg-primary text-white font-semibold rounded-lg hover:bg-orange-600 transition-colors duration-200 text-sm"
                >
                  Apply for {selectedLoan.label}
                </button>
              </div>

              {/* Right - Results */}
              <div className="p-6 lg:p-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">Your EMI Breakdown</h3>

                {/* EMI Amount - Big display */}
                <div className="text-center mb-6 p-4 bg-orange-50 rounded-xl border border-orange-200">
                  <div className="text-sm text-gray-500 mb-1">Monthly EMI</div>
                  <div className="text-4xl font-bold text-primary">{formatINR(Math.round(emi))}</div>
                  <div className="text-xs text-gray-500 mt-1">per month for {tenure} months</div>
                </div>

                {/* Donut chart visual */}
                <div className="flex items-center justify-center mb-6">
                  <div className="relative w-36 h-36">
                    <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
                      <circle cx="18" cy="18" r="15.9" fill="none" stroke="#E5E7EB" strokeWidth="3.5" />
                      <circle
                        cx="18" cy="18" r="15.9" fill="none"
                        stroke="#E07B1A"
                        strokeWidth="3.5"
                        strokeDasharray={`${principalPercent} ${interestPercent}`}
                        strokeLinecap="round"
                      />
                      <circle
                        cx="18" cy="18" r="15.9" fill="none"
                        stroke="#0066FF"
                        strokeWidth="3.5"
                        strokeDasharray={`${interestPercent} ${principalPercent}`}
                        strokeDashoffset={`-${principalPercent}`}
                        strokeLinecap="round"
                      />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <div className="text-xs text-gray-500">Total</div>
                      <div className="text-sm font-bold text-gray-900">{formatCurrency(Math.round(totalPayment))}</div>
                    </div>
                  </div>
                </div>

                {/* Breakdown */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-primary"></div>
                      <span className="text-sm text-gray-500">Principal Amount</span>
                    </div>
                    <span className="text-sm font-semibold text-gray-900">{formatINR(principal)}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                      <span className="text-sm text-gray-500">Total Interest</span>
                    </div>
                    <span className="text-sm font-semibold text-gray-900">{formatINR(Math.round(totalInterest))}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-orange-50 rounded-lg border border-orange-200">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-orange-400"></div>
                      <span className="text-sm font-medium text-gray-900">Total Payment</span>
                    </div>
                    <span className="text-sm font-bold text-primary">{formatINR(Math.round(totalPayment))}</span>
                  </div>
                </div>

                {/* Rate info */}
                <div className="mt-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <p className="text-xs text-gray-500">
                    <span className="font-semibold">Note:</span> This is an indicative EMI. Actual EMI may vary based on lender&apos;s terms. {selectedLoan.label} rates start from {selectedLoan.minRate}% p.a.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
